import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CloseButton from "../atoms/CloseButton";
import ContentContainer from "../atoms/ContentContainer";
import { color } from "../templates/ui";
import Title from "../atoms/Title";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { useWatchEscapeButton } from "../../hooks/useWatchEscapeButton";

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: ${props => props.theme.overlay};
  z-index: 6;
  transition: opacity 0.2s ease-in-out;
  opacity: ${props => (props.closed ? "0" : "1")};
`;

const StyledModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: 100%;
  background-color: white;
  border-radius: 7px;
  transform: translate(-50%, -50%);
  transition: opacity 0.2s ease-in-out;
  opacity: ${props => (props.closed ? "0" : "1")};
  overflow: hidden;
  z-index: 6;
  max-width: calc(100% - 30px);
  max-height: calc(100% - 40px);
  height: ${props => {
    if (props.height) {
      return props.height;
    }
    return "auto";
  }};

  @media only screen and (min-width: 768px) {
    max-width: 630px;
  }

  @media only screen and (min-width: 992px) {
    max-width: 780px;
  }

  ${props =>
    props.isCompact &&
    `
      @media only screen and (min-width: 768px) {
        max-width: 512px;
      }
  `};
`;

const ModalHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.tertiary};
  padding-bottom: 15px;
  margin: 20px 20px 0 20px;

  @media only screen and (min-width: 768px) {
    margin: 30px 30px 0 30px;
  }
`;

const StyledContentContainer = styled(ContentContainer)`
  height: calc(100% - 71px);
`;

const ModalContent = styled.div`
  padding: 15px 20px 20px;
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media only screen and (min-width: 768px) {
    padding: 15px 30px 30px;
  }
`;

const StyledTitle = styled(Title)`
  width: 100%;
  font-size: 17px;
  font-weight: 700;
  font-family: ${props => props.theme.fontHead};
  color: ${props => (props.redTitle ? color.error.base : props.theme.tertiary)};

  @media only screen and (min-width: 992px) {
    font-size: 20px;
  }
`;

const Modal = ({
  title,
  children,
  largeGutter,
  noGutter,
  mediumGutter,
  primary,
  hasShadow,
  redTitle,
  height,
  tag,
  className,
  closeable = true,
  onRequestClose,
  isCompact,
}) => {
  const [closed, setClosed] = useState(true);
  const { t } = useTranslation(["common"]);
  useWatchEscapeButton({
    when: () => setClosed,
    then: () => {
      if (onRequestClose) {
        onRequestClose();
      }
    },
  });

  useEffect(() => {
    if (closed === false) {
      return;
    }
    requestAnimationFrame(() => {
      if (children && closed) {
        setClosed(false);
      }
    });
  }, []);

  const close = () => {
    if (!closeable) {
      return;
    }
    setClosed(true);
    setTimeout(() => {
      onRequestClose();
    }, 100);
  };

  return (
    <Dialog.Root open={!closed} onOpenChange={setClosed}>
      <Dialog.Portal>
        <Dialog.Overlay asChild>
          <Backdrop closed={closed} onClick={close} />
        </Dialog.Overlay>
        <Dialog.Content>
          <StyledModal
            height={height}
            isCompact={isCompact}
            closed={closed}
            className={className}
          >
            <StyledContentContainer
              noGutter={noGutter}
              mediumGutter={mediumGutter}
              largeGutter={largeGutter}
              hasShadow={hasShadow}
            >
              <ModalHeader redTitle={redTitle}>
                <Dialog.Title asChild>
                  <StyledTitle tag={tag} redTitle={redTitle}>
                    {t(title)}
                  </StyledTitle>
                </Dialog.Title>
                {closeable && (
                  <Dialog.Close asChild>
                    <CloseButton
                      type="button"
                      primary={primary}
                      onClick={close}
                    />
                  </Dialog.Close>
                )}
              </ModalHeader>
              <ModalContent>{children}</ModalContent>
            </StyledContentContainer>
          </StyledModal>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;
